/**
 * TypeScript interfaces for Planned Works Module
 */

// Import User from main types
export interface User {
  id: number;
  username: string;
  first_name: string;
  last_name: string;
  email: string;
  role: string;
  department: string;
}

// Equipment interface
export interface Equipment {
  id: number;
  name: string;
  kks_code: string;
  site: number;
}

// Work status type
export type WorkStatus = 'draft' | 'proposed' | 'under_review' | 'approved' | 'scheduled' | 'ready_to_execute' | 'in_progress' | 'on_hold' | 'completed' | 'cancelled';

// Progress update interface
export interface WorkProgressUpdate {
  id: number;
  action: string;
  notes: string;
  created_at: string;
  created_by: User;
}

// Create request interfaces
export interface CreatePlannedWorkRequest {
  title: string;
  description: string;
  site: number;
  work_type_id: number | null;
  equipment_id?: number | null;
  priority: 'low' | 'medium' | 'high' | 'critical';
  assigned_to_id?: number | null;
  proposed_start_date?: Date | null;
  estimated_duration_hours: number;
  requires_permit: boolean;
  requires_isolation: boolean;
  requires_ppe: boolean;
  hot_work: boolean;
  confined_space: boolean;
  work_at_height: boolean;
  special_instructions: string;
  todos: CreateWorkTodoItemRequest[];
  work_package?: number;
}

export interface CreateWorkTodoItemRequest {
  title: string;
  description: string;
  estimated_duration_minutes: number;
  assigned_to_id?: number;
  requires_safety_check: boolean;
  depends_on?: number;
  order: number;
}

export interface CreateWorkPackageRequest {
  name: string;
  description: string;
  site: number;
  coordinator_id?: number | null;
  planned_start_date?: Date | null;
  planned_completion_date?: Date | null;
  max_concurrent_works: number;
  requires_coordination: boolean;
  special_instructions: string;
  works: number[];
}

// Base interfaces
export interface WorkType {
  id: number;
  name: string;
  code: string;
  description: string;
  category: string;
  default_permit_required: boolean;
  default_isolation_required: boolean;
  default_confined_space_entry: boolean;
  default_hot_work: boolean;
  default_duration_hours: number;
  default_crew_size: number;
  standard_work_instructions: string;
  standard_safety_requirements: string;
  standard_tools_required: string;
  standard_skills_required: string;
  requires_supervisor_approval: boolean;
  requires_site_manager_approval: boolean;
  requires_operations_approval: boolean;
  requires_safety_approval: boolean;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  required_approvals: string[];
  planned_works_count: number;
}

export interface WorkApproval {
  id: number;
  planned_work: number;
  approval_stage: string;
  approval_stage_display: string;
  approver: number;
  approver_name: string;
  status: 'pending' | 'approved' | 'rejected' | 'withdrawn';
  status_display: string;
  approved_at?: string;
  comments: string;
  conditions_applied: string;
  expiry_date?: string;
  created_at: string;
  updated_at: string;
}

export interface WorkTodoItem {
  id: number;
  planned_work: number;
  title: string;
  description: string;
  sequence_order: number;
  depends_on: number[];
  assigned_to?: number;
  assigned_to_user?: User;
  assigned_to_name?: string;
  estimated_duration_minutes: number;
  status: 'pending' | 'in_progress' | 'completed' | 'skipped' | 'blocked';
  status_display: string;
  completed_by?: number;
  completed_by_name?: string;
  completed_at?: string;
  actual_duration_minutes?: number;
  notes: string;
  blocked_reason: string;
  can_start: boolean;
  duration_variance_minutes?: number;
  requires_safety_check?: boolean;
  created_at: string;
  updated_at: string;
}

// Main PlannedWork interfaces
export interface PlannedWorkBase {
  id: number;
  work_number: string;
  title: string;
  description: string;
  site: number;
  site_name: string;
  site_code: string;
  work_type: number;
  work_type_name: string;
  work_type_category: string;
  proposed_start_date: string;
  proposed_end_date: string;
  estimated_duration_hours: number;
  status: 'draft' | 'proposed' | 'under_review' | 'approved' | 'scheduled' | 'ready_to_execute' | 'in_progress' | 'on_hold' | 'completed' | 'cancelled';
  status_display: string;
  priority: 'low' | 'normal' | 'high' | 'urgent' | 'emergency';
  priority_display: string;
  proposed_by: number;
  proposed_by_name: string;
  assigned_to?: number;
  assigned_to_name?: string;
  permit_required: boolean;
  isolation_required: boolean;
  confined_space_entry: boolean;
  hot_work: boolean;
  required_crew_size: number;
  days_until_proposed_start?: number;
  is_overdue: boolean;
  current_approval_stage?: string;
  created_at: string;
  updated_at: string;
}

export interface PlannedWorkListItem extends PlannedWorkBase {
  equipment_count: number;
  todo_items_count: number;
}

export interface PlannedWorkDetail extends PlannedWorkBase {
  equipment: number[];
  equipment_details: EquipmentDetail[];
  location?: number;
  location_name?: string;
  required_skills: string;
  required_tools: string;
  required_parts: string;
  special_instructions: string;
  safety_requirements: string;
  prerequisite_works: number[];
  related_maintenance_tasks: number[];
  generated_permit?: number;
  scheduled_start_date?: string;
  scheduled_end_date?: string;
  actual_start_date?: string;
  actual_end_date?: string;
  actual_duration_hours?: number;
  completion_percentage?: number;
  completion_notes: string;
  lessons_learned: string;
  approvals: WorkApproval[];
  todo_items: WorkTodoItem[];
  all_approvals_complete: boolean;
  can_start_execution: CanStartExecution;
  prerequisite_status: PrerequisiteStatus;
  work_type_details: WorkType;
}

// Work Package interfaces
export interface WorkPackage {
  id: number;
  package_number: string;
  name: string;
  description: string;
  package_name?: string;
  package_description?: string;
  site: number;
  site_name: string;
  planned_works: number[];
  works?: PlannedWorkListItem[];
  coordinator?: User;
  planned_start_date?: string;
  planned_completion_date?: string;
  scheduled_start_date?: string;
  scheduled_end_date?: string;
  actual_start_date?: string;
  actual_end_date?: string;
  package_coordinator?: number;
  package_coordinator_name?: string;
  assigned_crew: number[];
  assigned_crew_names: string[];
  total_estimated_hours: number;
  required_crew_size?: number;
  max_concurrent_works: number;
  requires_coordination: boolean;
  special_instructions?: string;
  status: 'draft' | 'planning' | 'ready' | 'in_progress' | 'completed' | 'cancelled';
  status_display: string;
  priority: 'low' | 'normal' | 'high' | 'urgent' | 'emergency';
  priority_display: string;
  coordination_notes: string;
  completion_percentage: number;
  can_start: boolean;
  planned_works_summary: WorkPackageWorkSummary[];
  created_by: number;
  created_at: string;
  updated_at: string;
}

// Supporting interfaces
export interface EquipmentDetail {
  id: number;
  equipment_id: string;
  name: string;
  kks_code: string;
  status: string;
  criticality: string;
}

export interface CanStartExecution {
  can_start: boolean;
  checks: {
    approved: boolean;
    all_approvals: boolean;
    no_pending_prerequisites: boolean;
    permit_ready: boolean;
  };
}

export interface PrerequisiteStatus {
  total: number;
  completed: number;
  pending: number;
  all_complete: boolean;
}

export interface WorkPackageWorkSummary {
  id: number;
  work_number: string;
  title: string;
  status: string;
  status_display: string;
  estimated_duration_hours: number;
  required_crew_size: number;
}

// Dashboard interfaces
export interface PlannedWorksDashboard {
  total_works: number;
  works_by_status: Record<string, number>;
  works_by_priority: Record<string, number>;
  overdue_works: number;
  pending_approvals: number;
  upcoming_works: number;
  active_packages: number;
  recent_completions: number;
}

// Form interfaces
export interface CreatePlannedWorkForm {
  title: string;
  description: string;
  site: number;
  work_type: number;
  equipment: number[];
  location?: number;
  proposed_start_date: string;
  proposed_end_date: string;
  estimated_duration_hours: number;
  priority: 'low' | 'normal' | 'high' | 'urgent' | 'emergency';
  assigned_to?: number;
  permit_required: boolean;
  isolation_required: boolean;
  confined_space_entry: boolean;
  hot_work: boolean;
  required_crew_size: number;
  required_skills: string;
  required_tools: string;
  required_parts: string;
  special_instructions: string;
  safety_requirements: string;
  prerequisite_works: number[];
  related_maintenance_tasks: number[];
}

export interface CreateWorkPackageForm {
  package_name: string;
  package_description: string;
  site: number;
  planned_works: number[];
  scheduled_start_date: string;
  scheduled_end_date: string;
  package_coordinator: number;
  assigned_crew: number[];
  total_estimated_hours: number;
  required_crew_size: number;
  priority: 'low' | 'normal' | 'high' | 'urgent' | 'emergency';
  coordination_notes: string;
  created_by: number;
}

export interface ApprovalForm {
  approval_stage: string;
  status: 'pending' | 'approved' | 'rejected' | 'withdrawn';
  comments: string;
  conditions_applied: string;
  expiry_date?: string;
}

export interface TodoCompletionForm {
  actual_duration_minutes?: number;
  notes: string;
}

export interface WorkCompletionForm {
  actual_duration_hours?: number;
  completion_notes: string;
  lessons_learned: string;
}

// Filter interfaces
export interface PlannedWorksFilters {
  site?: number;
  work_type?: number;
  status?: string;
  priority?: string;
  proposed_by?: number;
  assigned_to?: number;
  permit_required?: boolean;
  isolation_required?: boolean;
  overdue?: boolean;
  due_within_days?: number;
  search?: string;
}

export interface WorkPackageFilters {
  site?: number;
  status?: string;
  package_coordinator?: number;
  search?: string;
}

// Constants
export const WORK_STATUSES = [
  { value: 'draft', label: 'Draft', color: 'default' },
  { value: 'proposed', label: 'Proposed', color: 'info' },
  { value: 'under_review', label: 'Under Review', color: 'warning' },
  { value: 'approved', label: 'Approved', color: 'success' },
  { value: 'scheduled', label: 'Scheduled', color: 'primary' },
  { value: 'ready_to_execute', label: 'Ready to Execute', color: 'success' },
  { value: 'in_progress', label: 'In Progress', color: 'primary' },
  { value: 'on_hold', label: 'On Hold', color: 'warning' },
  { value: 'completed', label: 'Completed', color: 'success' },
  { value: 'cancelled', label: 'Cancelled', color: 'error' }
] as const;

export const WORK_PRIORITIES = [
  { value: 'low', label: 'Low', color: 'default' },
  { value: 'normal', label: 'Normal', color: 'info' },
  { value: 'high', label: 'High', color: 'warning' },
  { value: 'urgent', label: 'Urgent', color: 'error' },
  { value: 'emergency', label: 'Emergency', color: 'error' }
] as const;

export const WORK_CATEGORIES = [
  { value: 'maintenance', label: 'Maintenance' },
  { value: 'inspection', label: 'Inspection' },
  { value: 'repair', label: 'Repair' },
  { value: 'upgrade', label: 'Upgrade' },
  { value: 'modification', label: 'Modification' },
  { value: 'testing', label: 'Testing' },
  { value: 'commissioning', label: 'Commissioning' },
  { value: 'emergency', label: 'Emergency' },
  { value: 'project', label: 'Project Work' },
  { value: 'other', label: 'Other' }
] as const;

export const TODO_STATUSES = [
  { value: 'pending', label: 'Pending', color: 'default' },
  { value: 'in_progress', label: 'In Progress', color: 'primary' },
  { value: 'completed', label: 'Completed', color: 'success' },
  { value: 'skipped', label: 'Skipped', color: 'warning' },
  { value: 'blocked', label: 'Blocked', color: 'error' }
] as const;

export const APPROVAL_STAGES = [
  { value: 'supervisor', label: 'Supervisor Approval' },
  { value: 'site_manager', label: 'Site Manager Approval' },
  { value: 'operations', label: 'Operations Approval' },
  { value: 'safety', label: 'Safety Approval' },
  { value: 'engineering', label: 'Engineering Approval' },
  { value: 'management', label: 'Management Approval' }
] as const;

export const APPROVAL_STATUSES = [
  { value: 'pending', label: 'Pending Review', color: 'warning' },
  { value: 'approved', label: 'Approved', color: 'success' },
  { value: 'rejected', label: 'Rejected', color: 'error' },
  { value: 'withdrawn', label: 'Withdrawn', color: 'default' }
] as const;

export const PACKAGE_STATUSES = [
  { value: 'draft', label: 'Draft', color: 'default' },
  { value: 'planning', label: 'Planning', color: 'info' },
  { value: 'ready', label: 'Ready', color: 'success' },
  { value: 'in_progress', label: 'In Progress', color: 'primary' },
  { value: 'completed', label: 'Completed', color: 'success' },
  { value: 'cancelled', label: 'Cancelled', color: 'error' }
] as const;

// Helper functions
export const getStatusColor = (status: string, statusList: readonly any[]) => {
  const statusObj = statusList.find(s => s.value === status);
  return statusObj?.color || 'default';
};

export const getStatusLabel = (status: string, statusList: readonly any[]) => {
  const statusObj = statusList.find(s => s.value === status);
  return statusObj?.label || status;
};

export const getPriorityColor = (priority: string) => {
  return getStatusColor(priority, WORK_PRIORITIES);
};

export const getWorkStatusColor = (status: string) => {
  return getStatusColor(status, WORK_STATUSES);
};

export const getTodoStatusColor = (status: string) => {
  return getStatusColor(status, TODO_STATUSES);
};

export const getApprovalStatusColor = (status: string) => {
  return getStatusColor(status, APPROVAL_STATUSES);
};

export const getPackageStatusColor = (status: string) => {
  return getStatusColor(status, PACKAGE_STATUSES);
};
